import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, Star, Truck, Shield, Headphones, Zap, ChevronLeft, ChevronRight } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import SEO from '../components/SEO';
import { categories, Product } from '../data/products';
import { listProducts } from '../api/products';
import { buildApiUrl } from '../config';

// Newsletter Subscription Component
function NewsletterSection() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      const response = await fetch(buildApiUrl('/api/newsletter/subscribe'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({ type: 'success', text: data.message || 'Successfully subscribed to newsletter!' });
        setEmail('');
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to subscribe. Please try again.' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to subscribe. Please try again.' });
    } finally {
      setLoading(false);
      setTimeout(() => setMessage(null), 5000);
    }
  };

  return (
    <section className="py-12 bg-[#F6F4EF]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-[#4F36F6] rounded-2xl sm:rounded-3xl p-6 sm:p-10 md:p-12 flex flex-col lg:flex-row lg:items-center justify-between gap-6 shadow-xl relative overflow-hidden">
          {/* Subtle background glow */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none"></div>

          <div className="max-w-xl relative z-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight mb-2">
              Get the first look at new drops
            </h2>
            <p className="text-indigo-100 text-sm sm:text-base leading-relaxed">
              Restocks, new launches and member-only pricing — one email a week, nothing else.
            </p>
          </div>

          <div className="relative z-10 w-full lg:w-auto">
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                required
                className="w-full sm:w-72 md:w-80 px-4 py-3.5 bg-white text-gray-900 rounded-xl border-0 focus:outline-none focus:ring-2 focus:ring-white text-sm placeholder-gray-400 shadow-sm"
                disabled={loading}
              />
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-3.5 bg-black hover:bg-gray-900 active:scale-95 text-white font-bold text-sm rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center whitespace-nowrap shadow-md"
              >
                {loading ? 'Subscribing...' : 'Subscribe'}
              </button>
            </form>
            {message && (
              <div className={`mt-3 text-xs sm:text-sm font-medium ${message.type === 'success' ? 'text-green-200' : 'text-yellow-200'}`}>
                {message.text}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

interface Banner {
  _id: string;
  title?: string;
  description?: string;
  image: string;
  mobileImage?: string;
  type: 'hero' | 'category' | 'promotional';
  link?: string;
  position: number;
  isActive: boolean;
  showTitle?: boolean;
  productSlug?: string;
  productId?: any;
}

// Category icon and image mapping
const categoryIcons: { [key: string]: { icon: string, image: string } } = {
  'speakers': { icon: '🔊', image: '/images/banners/banner_enhanced_1.png' },
  'chargers': { icon: '🔌', image: '/images/banners/banner_enhanced_4.png' },
  'headphones': { icon: '🎧', image: '/images/products/product_image_9.jpg' },
  'accessories': { icon: '📱', image: '/images/banners/banner_enhanced_2.png' },
  'powerbanks': { icon: '🔋', image: '/images/banners/banner_enhanced_3.png' },
  'power banks': { icon: '🔋', image: '/images/banners/banner_enhanced_3.png' },
  'cables': { icon: '🔗', image: '/images/banners/banner_enhanced_5.png' },
  'default': { icon: '📦', image: '/images/products/product_image_1.jpg' }
};

const getCategoryInfo = (categoryName: string) => {
  const key = categoryName.toLowerCase();
  return categoryIcons[key] || categoryIcons[Object.keys(categoryIcons).find(k => key.includes(k)) || 'default'] || categoryIcons['default'];
};

const formatCategoryName = (category: string): string => {
  return category
    .split(/[-_\s]+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const HomePage: React.FC = () => {
  const location = useLocation();
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([]);
  const [banners, setBanners] = useState<Banner[]>([]);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [dynamicCategories, setDynamicCategories] = useState<Array<{ id: string; name: string; icon: string; image: string }>>([]);
  const [loadingFeatured, setLoadingFeatured] = useState(false);
  const [hasMoreFeatured, setHasMoreFeatured] = useState(true);
  const [featuredPage, setFeaturedPage] = useState(1);
  const featuredPerPage = 8;

  // Scroll to top when component mounts or route changes (including back/forward navigation)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  // Load featured products with lazy loading
  const loadFeaturedProducts = async (page: number, append: boolean = false) => {
    setLoadingFeatured(true);
    try {
      const data = await listProducts({
        featured: true,
        sort: 'rating',
        page,
        limit: featuredPerPage
      });

      if (append) {
        setFeaturedProducts(prev => [...prev, ...data.items]);
      } else {
        setFeaturedProducts(data.items);
      }

      setHasMoreFeatured(data.items.length === featuredPerPage && data.page < data.pages);
      setFeaturedPage(page);
    } catch (error) {
      console.error('Failed to load featured products:', error);
      if (!append) setFeaturedProducts([]);
    } finally {
      setLoadingFeatured(false);
    }
  };

  useEffect(() => {
    loadFeaturedProducts(1, false);

    // Fetch active hero banners for carousel
    const bannerUrl = buildApiUrl('/api/banners?type=hero&active=true');

    const localImages = ['/images/banners/banner_composite_2.png', '/images/banners/banner_speaker_original.png', '/images/banners/banner_enhanced_2.png', '/images/banners/banner_enhanced_3.png', '/images/banners/banner_enhanced_4.png', '/images/banners/banner_enhanced_5.png', '/images/banners/banner_enhanced_6.png'];
    const defaultFallbackBanners: Banner[] = [
      {
        _id: '0',
        title: 'For Everything and Everyone',
        description: 'Discover our premium ecosystem of tech accessories designed to elevate your everyday experience.',
        image: localImages[0],
        type: 'hero',
        position: 0,
        link: '/products',
        isActive: true
      },
      {
        _id: '1',
        title: 'Wireless Multifunction Speaker',
        description: '4 in one multifunction device featuring bluetooth speaker, 15w wireless fast charger, mood lighting and roatating phone stand',
        image: localImages[1],
        type: 'hero',
        position: 1,
        link: '/products?category=speakers',
        isActive: true
      },
      {
        _id: '2',
        title: 'Secure & Simple Navigation.',
        description: 'Cnine Vehicle Navigation Car Holder. Universal 360° rotation for perfect viewing angles. Sturdy grip and easy one-hand operation for safe driving.',
        image: localImages[2],
        type: 'hero',
        position: 2,
        link: '/products?category=accessories',
        isActive: true
      },
      {
        _id: '3',
        title: 'Power You Can Depend On.',
        description: 'Nexcharge MP350 10000mAh Power Bank. 35W fast charging in a pocket-sized design. Keep your devices powered up anywhere you go.',
        image: localImages[3],
        type: 'hero',
        position: 3,
        link: '/products?category=powerbanks',
        isActive: true
      },
      {
        _id: '4',
        title: 'Charge Your Phone Safely!',
        description: '100W Rapid Power Car Charger. Dual-port fast charging for all your devices. Advanced temperature control and short-circuit protection for complete peace of mind.',
        image: localImages[4],
        type: 'hero',
        position: 4,
        link: '/products?category=chargers',
        isActive: true
      },
      {
        _id: '5',
        title: 'Ultra-Fast Data Transfer.',
        description: 'Stream Digital 100W Type-C to Type-C Data Cable. Built for speed and durability, ensuring your devices sync and charge flawlessly.',
        image: localImages[5],
        type: 'hero',
        position: 5,
        link: '/products?category=cables',
        isActive: true
      },
      {
        _id: '6',
        title: 'Stay Hydrated, Stay Connected.',
        description: 'Magnetic Sports Water Bottle with Phone Holder. Leak-proof design perfect for your active lifestyle.',
        image: localImages[6],
        type: 'hero',
        position: 6,
        link: '/products?category=accessories',
        isActive: true
      }
    ];

    fetch(bannerUrl)
      .then(res => {
        if (!res.ok) {
          console.error('Failed to fetch hero banners:', res.status);
          return [];
        }
        return res.json();
      })
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          const sortedBanners = data.sort((a: Banner, b: Banner) => (a.position || 0) - (b.position || 0));
          setBanners(sortedBanners);
        } else {
          setBanners(defaultFallbackBanners);
        }
      })
      .catch((error) => {
        console.error('Error fetching hero banners:', error);
        setBanners(defaultFallbackBanners);
      });

    // Fetch all products to get unique categories
    listProducts({ limit: 1000 })
      .then((data) => {
        const uniqueCategories = Array.from(new Set(data.items.map(p => p.category).filter(Boolean))) as string[];
        const categoryList = uniqueCategories
          .filter(cat => cat && cat.trim() !== '') // Filter out empty categories
          .map(cat => ({
            id: cat, // Use original category value to match database
            name: formatCategoryName(cat),
            icon: getCategoryInfo(cat).icon,
            image: getCategoryInfo(cat).image
          }))
          .filter(cat => cat.name && cat.name.trim() !== ''); // Filter out categories with empty names
        setDynamicCategories(categoryList);
      })
      .catch(() => {
        // Fallback to default categories if API fails
        setDynamicCategories(categories.map(c => ({ ...c, image: getCategoryInfo(c.id).image })));
      });
  }, []);

  // Auto-rotate banners
  useEffect(() => {
    if (banners.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentBannerIndex((prev) => (prev + 1) % banners.length);
    }, 5000); // Change banner every 5 seconds
    return () => clearInterval(interval);
  }, [banners.length]);

  return (
    <div className="min-h-screen">
      <SEO
        title="Premium Tech Accessories"
        description="Shop the latest gadgets at cnine. Discover premium speakers, chargers, headphones, power banks, and more with free shipping and warranty."
        keywords="c nine, c9, cnine, tech accessories, speakers, chargers, headphones, power banks, cables, bluetooth, wireless"
      />
      {/* Banner Carousel Section */}
      {banners.length > 0 && (
        <section className="relative w-full overflow-hidden bg-[#f1f3f5] group">
          <div className="relative w-full aspect-square xs:aspect-[4/3] sm:aspect-auto sm:h-[380px] md:h-[480px] lg:h-[550px] overflow-hidden">
            {banners.map((banner, index) => {
              const targetLink = banner.link || (
                banner.productSlug ? `/products/${banner.productSlug}` : (
                  banner.productId ? `/products/${typeof banner.productId === 'object' ? (banner.productId.id || banner.productId._id || banner.productId.slug) : banner.productId}` : '/products'
                )
              );

              const hasText = banner.showTitle !== false && (Boolean(banner.title) || Boolean(banner.description));

              return (
                <div
                  key={banner._id}
                  className={`absolute inset-0 transition-opacity duration-1000 ease-in-out flex items-center justify-center ${index === currentBannerIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'
                    }`}
                >
                  {hasText ? (
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full h-full flex flex-col-reverse md:flex-row items-center justify-between">
                      {/* Left Side: Text */}
                      <div className="md:w-1/2 flex flex-col justify-center items-start pb-6 md:pb-0 z-20">
                        {banner.title && (
                          <h2 className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-3 sm:mb-5 tracking-tight leading-tight transition-all duration-700 delay-100 ${index === currentBannerIndex ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
                            {banner.title}
                          </h2>
                        )}
                        {banner.description && (
                          <p className={`text-sm sm:text-base text-gray-600 mb-6 max-w-md transition-all duration-700 delay-300 leading-relaxed ${index === currentBannerIndex ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
                            {banner.description}
                          </p>
                        )}
                        <div className={`flex flex-wrap gap-4 transition-all duration-700 delay-500 ${index === currentBannerIndex ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
                          <Link
                            to={targetLink}
                            className="px-6 py-2.5 sm:px-8 sm:py-3 bg-[#4554a4] text-white font-bold rounded hover:bg-[#374a8a] transition-colors uppercase text-xs sm:text-sm tracking-wide"
                          >
                            TO SHOP
                          </Link>
                          <Link
                            to={targetLink}
                            className="px-6 py-2.5 sm:px-8 sm:py-3 bg-transparent text-gray-900 border border-gray-200 font-bold rounded hover:border-gray-900 transition-colors uppercase text-xs sm:text-sm tracking-wide"
                          >
                            READ MORE
                          </Link>
                        </div>
                      </div>

                      {/* Right Side: Image */}
                      <div className="md:w-1/2 flex-1 w-full h-full relative flex items-center justify-center transition-all duration-1000 delay-200">
                        <Link to={targetLink} className="block w-full h-full flex items-center justify-center relative group/img">
                          {banner.image && (
                            <picture className="w-full h-full flex items-center justify-center">
                              {banner.mobileImage && (
                                <source media="(max-width: 639px)" srcSet={banner.mobileImage} />
                              )}
                              <img
                                src={banner.image}
                                alt={banner.title || 'Banner'}
                                className="max-h-full max-w-full object-contain scale-100 group-hover/img:scale-105 transition-transform duration-700 relative z-10"
                                onError={(e) => console.error('Failed to load banner image:', banner.image)}
                              />
                            </picture>
                          )}
                        </Link>
                      </div>
                    </div>
                  ) : (
                    /* Full Graphic Banner (No Text Overlay) */
                    <div className="w-full h-full flex items-center justify-center">
                      <Link to={targetLink} className="block w-full h-full relative group/img flex items-center justify-center">
                        {banner.image && (
                          <picture className="w-full h-full flex items-center justify-center">
                            {banner.mobileImage && (
                              <source media="(max-width: 639px)" srcSet={banner.mobileImage} />
                            )}
                            <img
                              src={banner.image}
                              alt={banner.title || 'Banner'}
                              className="w-full h-full object-cover sm:object-contain scale-100 group-hover/img:scale-105 transition-transform duration-700 relative z-10"
                              onError={(e) => console.error('Failed to load banner image:', banner.image)}
                            />
                          </picture>
                        )}
                      </Link>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Navigation Arrows */}
            {banners.length > 1 && (
              <>
                <button
                  onClick={() => setCurrentBannerIndex((prev) => (prev - 1 + banners.length) % banners.length)}
                  className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 bg-white/50 hover:bg-white border border-gray-200 text-gray-800 rounded-full p-2 sm:p-3 transition-all duration-300 z-30 touch-manipulation opacity-0 group-hover:opacity-100 -translate-x-4 group-hover:translate-x-0 shadow-md"
                  aria-label="Previous banner"
                >
                  <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8" />
                </button>
                <button
                  onClick={() => setCurrentBannerIndex((prev) => (prev + 1) % banners.length)}
                  className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 bg-white/50 hover:bg-white border border-gray-200 text-gray-800 rounded-full p-2 sm:p-3 transition-all duration-300 z-30 touch-manipulation opacity-0 group-hover:opacity-100 translate-x-4 group-hover:translate-x-0 shadow-md"
                  aria-label="Next banner"
                >
                  <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8" />
                </button>
              </>
            )}

            {/* Dots Indicator */}
            {banners.length > 1 && (
              <div className="absolute bottom-4 sm:bottom-8 left-1/2 -translate-x-1/2 flex space-x-2 sm:space-x-3 z-30">
                {banners.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentBannerIndex(index)}
                    className={`relative overflow-hidden rounded-full transition-all duration-500 touch-manipulation ${index === currentBannerIndex
                      ? 'w-8 sm:w-12 h-2 sm:h-2 bg-[#4554a4]'
                      : 'w-2 sm:w-2 h-2 sm:h-2 bg-gray-300 hover:bg-gray-400'
                      }`}
                    aria-label={`Go to banner ${index + 1}`}
                  >
                    {index === currentBannerIndex && (
                      <span className="absolute inset-0 bg-[#374a8a] rounded-full animate-[progress_5s_linear]" style={{ width: '100%', transformOrigin: 'left' }}></span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>
      )}



      {/* Features Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Truck className="w-8 h-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Free Shipping</h3>
              <p className="text-gray-600">Free delivery on orders above ₹999</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">1 Year Warranty</h3>
              <p className="text-gray-600">Comprehensive warranty on all products</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Headphones className="w-8 h-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">24/7 Support</h3>
              <p className="text-gray-600">Round-the-clock customer support</p>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-16 sm:py-24 relative overflow-hidden bg-[#F6F4EF]">
        {/* Subtle background decoration */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary-50 rounded-full blur-3xl opacity-50 -translate-y-1/2 translate-x-1/4 pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-blue-50 rounded-full blur-3xl opacity-50 translate-y-1/2 -translate-x-1/4 pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 sm:mb-16 gap-4">
            <div className="max-w-2xl">
              <span className="text-primary-600 font-semibold tracking-wider uppercase text-sm mb-2 block">Categories</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">Shop by Category</h2>
            </div>
            <Link to="/products" className="group flex items-center text-primary-600 font-semibold hover:text-primary-700 transition-colors">
              View All Categories
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 sm:gap-6">
            {(dynamicCategories.length > 0 ? dynamicCategories : categories)
              .filter(category => category && category.id && category.name)
              .map((category: any) => (
                <Link
                  key={category.id}
                  to={`/products?category=${category.id}`}
                  className="group relative bg-white rounded-2xl p-6 text-center hover:shadow-xl transition-all duration-500 border border-gray-100 hover:border-primary-200 overflow-hidden"
                >
                  {/* Hover gradient background */}
                  <div className="absolute inset-0 bg-gradient-to-br from-primary-50/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                  <div className="relative z-10">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto bg-gray-50 rounded-2xl overflow-hidden flex items-center justify-center mb-4 sm:mb-5 group-hover:-translate-y-2 group-hover:shadow-md transition-all duration-300 border border-gray-100 group-hover:border-primary-100 group-hover:bg-white">
                      <img src={category.image || '/images/products/product_image_1.jpg'} alt={category.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                    </div>
                    <h3 className="text-sm sm:text-base font-bold text-gray-900 group-hover:text-primary-600 transition-colors">
                      {category.name}
                    </h3>
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </section>

      {/* Featured Products Section */}
      <section className="py-16 sm:py-24 bg-[#F6F4EF] border-t border-[#DEDACF]/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 sm:mb-16 gap-4">
            <div className="max-w-2xl">
              <span className="text-primary-600 font-semibold tracking-wider uppercase text-sm mb-2 block">Top Picks</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">Featured Products</h2>
            </div>
          </div>

          {featuredProducts.length === 0 && !loadingFeatured ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
              <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Star className="w-10 h-10 text-gray-300" />
              </div>
              <p className="text-lg text-gray-500 font-medium">No featured products available at the moment.</p>
            </div>
          ) : (
            <>
              {/* Desktop Grid Layout / Mobile Horizontal Scroll */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {featuredProducts.map((product) => (
                  <div key={product.id} className="w-full">
                    <ProductCard product={product} variant="default" />
                  </div>
                ))}
              </div>

              {/* Load More Button */}
              {hasMoreFeatured && (
                <div className="text-center mt-12 sm:mt-16">
                  <button
                    onClick={() => loadFeaturedProducts(featuredPage + 1, true)}
                    disabled={loadingFeatured}
                    className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-white bg-gray-900 border border-transparent rounded-full hover:bg-gray-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                  >
                    {loadingFeatured ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Loading...
                      </>
                    ) : (
                      <>
                        Load More Products
                        <ArrowRight className="ml-2 w-5 h-5" />
                      </>
                    )}
                  </button>
                </div>
              )}

              <div className={`text-center ${hasMoreFeatured ? 'mt-6' : 'mt-12 sm:mt-16'}`}>
                <Link
                  to="/products"
                  className="inline-flex items-center text-gray-600 font-semibold hover:text-primary-600 transition-colors"
                >
                  View All Products
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Feature Strip Section (Why Choose c nine) */}
      <section className="bg-[#0F1115] py-12 sm:py-16 border-y border-[#2A2E38]/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">Why Choose c nine?</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {/* Item 1 */}
            <div className="flex items-start gap-3.5">
              <div className="w-10 h-10 rounded-xl bg-[#4B3AF0]/20 border border-[#4B3AF0]/40 flex items-center justify-center text-[#B9AEFF] flex-shrink-0 mt-0.5">
                <Star className="w-5 h-5" />
              </div>
              <div>
                <h4 className="text-white font-semibold text-sm sm:text-base mb-1">Handpicked quality</h4>
                <p className="text-gray-400 text-xs leading-relaxed">
                  Every product tested for build and battery life before it ships.
                </p>
              </div>
            </div>

            {/* Item 2 */}
            <div className="flex items-start gap-3.5">
              <div className="w-10 h-10 rounded-xl bg-[#4B3AF0]/20 border border-[#4B3AF0]/40 flex items-center justify-center text-[#B9AEFF] flex-shrink-0 mt-0.5">
                <Zap className="w-5 h-5" />
              </div>
              <div>
                <h4 className="text-white font-semibold text-sm sm:text-base mb-1">Fast delivery</h4>
                <p className="text-gray-400 text-xs leading-relaxed">
                  Dispatched within 24 hours, tracked door to door.
                </p>
              </div>
            </div>

            {/* Item 3 */}
            <div className="flex items-start gap-3.5">
              <div className="w-10 h-10 rounded-xl bg-[#4B3AF0]/20 border border-[#4B3AF0]/40 flex items-center justify-center text-[#B9AEFF] flex-shrink-0 mt-0.5">
                <Shield className="w-5 h-5" />
              </div>
              <div>
                <h4 className="text-white font-semibold text-sm sm:text-base mb-1">Secure checkout</h4>
                <p className="text-gray-400 text-xs leading-relaxed">
                  Encrypted payments, no card details stored.
                </p>
              </div>
            </div>

            {/* Item 4 */}
            <div className="flex items-start gap-3.5">
              <div className="w-10 h-10 rounded-xl bg-[#4B3AF0]/20 border border-[#4B3AF0]/40 flex items-center justify-center text-[#B9AEFF] flex-shrink-0 mt-0.5">
                <Headphones className="w-5 h-5" />
              </div>
              <div>
                <h4 className="text-white font-semibold text-sm sm:text-base mb-1">Real support</h4>
                <p className="text-gray-400 text-xs leading-relaxed">
                  Reach a person, not a bot, within one business day.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <NewsletterSection />
    </div>
  );
};

export default HomePage;
